class BundleForm extends HTMLElement {
  constructor() {
    super();
    this.quantities = {};
    this.minTotal = 2;
    this.maxTotal = 6;
    this.products = [];
    this.basePrice = 16;
    this.discounts = {
      2: 0.0625,
      3: 0.0833,
      4: 0.09375,
      5: 0.125,
      6: 0.1458
    };
    this.currencySymbol = '$';
  }

  async connectedCallback() {
    this.setCurrencySymbol();
    this.loadBundleVariants();
    await this.loadProducts();
    this.initializeQuantities();
    this.setupEventListeners();
  }

  loadBundleVariants() {
    const variants = {
      2: this.querySelector('[data-bundle-variant="bundle-of-2"]')?.value,
      3: this.querySelector('[data-bundle-variant="bundle-of-3"]')?.value,
      4: this.querySelector('[data-bundle-variant="bundle-of-4"]')?.value,
      5: this.querySelector('[data-bundle-variant="bundle-of-5"]')?.value,
      6: this.querySelector('[data-bundle-variant="bundle-of-6"]')?.value
    };
    this.bundleVariants = variants;
  }

  setCurrencySymbol() {
    const currency = this.getAttribute('data-currency') || 'USD';
    const symbolMap = {
      'USD': '$',
      'SGD': 'S$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'AUD': 'A$',
      'CAD': 'C$'
    };
    this.currencySymbol = symbolMap[currency] || currency;
  }

  async loadProducts() {
    const handles = ['planner-001-soft-tide', 'planner-001-cobblestone', 'planner001-snowflake'];

    // 1. Immediately show the UI using local data (like handles)
    // 2. Fetch inventory in the background
    const productPromises = handles.map(handle =>
      fetch(`/products/${handle}.js`).then(res => res.json())
    );

    // Use Promise.allSettled so one failed product doesn't break the whole bundle
    const results = await Promise.allSettled(productPromises);

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        const product = result.value;
        const handle = handles[index];

        this.products.push({
          handle: handle,
          available: product.available,
          title: product.title,
          variantId: product.variants[0].id
        });

        this.updateStockStatus(handle, product.available);
      }
    });

    this.updateTotal(); // Refresh UI once stock status is known
  }

  updateStockStatus(handle, available) {
    // Search only inside this component (this) instead of the whole document
    const row = this.querySelector(`[data-product-handle="${handle}"]`);
    if (!row) return;

    const statusElement = row.querySelector('.bundle-stock-status');
    if (statusElement && !available) {
      statusElement.textContent = '(Sold Out)';
      // Use a CSS class instead of inline styles for better performance
      statusElement.classList.add('is-sold-out');

      const increaseBtn = row.querySelector('[data-action="increase"]');
      if (increaseBtn) increaseBtn.disabled = true;
    }
  }

  setupEventListeners() {
    this.querySelectorAll('[data-action="decrease"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleQuantityChange(e, -1));
    });

    this.querySelectorAll('[data-action="increase"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleQuantityChange(e, 1));
    });

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  initializeQuantities() {
    this.products.forEach(product => {
      if (product.handle === 'planner-001-soft-tide') {
        this.quantities[product.handle] = 0;
      } else {
        this.quantities[product.handle] = 1;
      }
    });
    this.updateAllUI();
    this.updateTotal();
  }

  updateAllUI() {
    this.products.forEach(product => {
      this.updateUI(product.handle, this.quantities[product.handle]);
    });
  }

  handleQuantityChange(event, delta) {
    const handle = event.target.closest('[data-product-handle]').dataset.productHandle;
    const current = this.quantities[handle] || 0;
    const newValue = Math.max(0, Math.min(this.maxTotal, current + delta));

    this.quantities[handle] = newValue;
    this.updateUI(handle, newValue);
    this.updateTotal();
  }

  updateUI(handle, quantity) {
    const input = this.querySelector(`input[data-product-handle="${handle}"]`);
    if (input) {
      input.value = quantity;
    }
  }

  showStockErrorNotification(handle) {
    const product = this.products.find(p => p.handle === handle);
    const productName = product?.title || handle;
    const message = `${productName} is now out of stock. Please refresh the page.`;

    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background-color: #ff6b6b;
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 4px;
      font-size: 0.95rem;
      z-index: 9999;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      font-family: 'Tabular', sans-serif;
    `;
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 5000);
  }

  updateTotal() {
    const total = Object.values(this.quantities).reduce((sum, qty) => sum + qty, 0);
    const counter = this.querySelector('[data-total-counter]');

    if (counter) {
      let priceText = '';
      if (total >= this.minTotal && total <= this.maxTotal) {
        const subtotal = total * this.basePrice;
        const discount = this.discounts[total];
        const discountAmount = subtotal * discount;
        const finalPrice = subtotal - discountAmount;
        const discountPercent = Math.round(discount * 100);

        priceText = `${total} items: <s>${this.currencySymbol}${subtotal}</s> ${this.currencySymbol}${finalPrice.toFixed(0)}`;
        counter.innerHTML = priceText;
      } else if (total === 0) {
        counter.textContent = '0 items';
      } else {
        counter.textContent = `${total} items (min 2, max 6)`;
      }
    }

    // Disable increase buttons if total is at max
    const increaseButtons = this.querySelectorAll('[data-action="increase"]');
    increaseButtons.forEach(btn => {
      btn.disabled = total >= this.maxTotal;
    });

    // Update existing price-item elements in price__sale
    if (total >= this.minTotal && total <= this.maxTotal) {
      const subtotal = total * this.basePrice;
      const discount = this.discounts[total];
      const discountAmount = subtotal * discount;
      const finalPrice = subtotal - discountAmount;

      // Update regular price (compare-at price) - inside <s> tag in price__sale
      const priceSale = document.querySelector('.price__sale');
      if (priceSale) {
        const regularPrice = priceSale.querySelector('s.price-item.price-item--regular');
        if (regularPrice) {
          regularPrice.textContent = `${this.currencySymbol}${subtotal}`;
        }

        // Update sale price
        const salePrice = priceSale.querySelector('.price-item.price-item--sale.price-item--last');
        if (salePrice) {
          salePrice.textContent = `${this.currencySymbol}${finalPrice.toFixed(0)}`;
        }
      }
    }

    const button = this.querySelector('button[type="submit"]');
    if (button) {
      // Check if any selected items are sold out
      const hasSoldOutSelected = this.products.some(product =>
        this.quantities[product.handle] > 0 && !product.available
      );

      const isValid = total >= this.minTotal && total <= this.maxTotal && !hasSoldOutSelected;
      button.disabled = !isValid;
      button.classList.toggle('bundle-button--disabled', !isValid);
    }
  }

  async handleSubmit(event) {
    event.preventDefault();

    const total = Object.values(this.quantities).reduce((sum, qty) => sum + qty, 0);
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const submitButton = this.querySelector('button[type="submit"]');

    // 1. Loading State
    if (submitButton) {
      submitButton.setAttribute('aria-disabled', true);
      submitButton.classList.add('loading');
      const spinner = submitButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    // Check if selected items are still in stock
    const selectedHandles = this.products
      .filter(product => this.quantities[product.handle] > 0)
      .map(product => product.handle);

    for (const handle of selectedHandles) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        const product = await response.json();
        if (!product.available) {
          this.showStockErrorNotification(handle);
          if (submitButton) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading');
            const spinner = submitButton.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          }
          return;
        }
      } catch (error) {
        console.error('Error checking stock for', handle, error);
      }
    }

    const formData = new FormData();
    formData.append('id', this.bundleVariants[total]);
    formData.append('quantity', 1);

    const configItems = [];
    this.products.forEach(product => {
      const qty = this.quantities[product.handle];
      if (qty > 0) {
        configItems.push(`${product.title}: ${qty}`);
        const flowKey = `_qty_${product.handle.replace(/[^a-zA-Z0-9]/g, '_')}`;
        formData.append(`properties[${flowKey}]`, qty.toString());
      }
    });
    formData.append('properties[Configuration]', configItems.join(', '));

    // 2. Sections Request
    if (cart) {
      cart.getSectionsToRender().forEach((section) => {
        formData.append('sections', section.id);
      });
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: formData
      });

      const responseData = await response.json();

      if (responseData.status) throw new Error(responseData.description);

      // 3. THE CRITICAL FIX: Bypass the crash
      if (cart) {
        // Ensure the key exists for the notification script
        const parsedState = responseData.items ? responseData.items[0] : responseData;
        parsedState.sections = responseData.sections;

        // We manually check if the target IDs exist in the DOM before letting the theme script run.
        // If the specific product ID is missing, we create a generic ID to prevent the innerHTML error.
        const targetId = `cart-notification-product-${parsedState.key}`;
        if (!document.getElementById('cart-notification-product')) {
          console.error('Missing cart-notification-product container');
        }

        try {
          cart.renderContents(parsedState);
        } catch (e) {
          console.warn('Theme renderContents failed, attempting manual bubble update:', e);
          // If the theme script still fails, manually update the bubble at least
          if (responseData.sections?.['cart-icon-bubble']) {
            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble) bubble.innerHTML = responseData.sections['cart-icon-bubble'];
            cart.open(); // Try to open it anyway
          }
        }
      }

      // 4. Update the bubble via PubSub
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-form',
          productVariantId: formData.get('id'),
          cartData: responseData
        });
      }

    } catch (error) {
      console.error('Bundle add to cart error:', error);
    } finally {
      if (submitButton) {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
        const spinner = submitButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
    }
  }
}

customElements.define('bundle-form', BundleForm);
